import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Handshake,
  ListOrdered,
  Shirt,
  FileText,
  Banknote,
  ArrowRight,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';

// NOTE FOR DRAFTFC: placeholder copy — review wording before creators are
// invited in. Figures (25%, weekly Mondays, offers) match the live config.

const THINGS = [
  {
    icon: ListOrdered,
    title: 'Publish your draft rankings',
    body: 'Rank the full player pool your way. Members can pick your rankings inside the Draft Room and Live Draft to guide their picks.',
    action: { label: 'Set rankings', path: '/rankings' },
  },
  {
    icon: Shirt,
    title: 'Set predicted lineups',
    body: 'Call the starting XIs each gameweek with the same pitch tools DraftFC uses for its official predictions.',
    action: { label: 'Set lineups', path: '/lineups' },
  },
  {
    icon: FileText,
    title: 'Write for the DraftFC blog',
    body: 'Draft articles in the editor and submit them for review — we publish and promote them under your name.',
    action: { label: 'Write a post', path: '/blog' },
  },
];

export default function CommercialOffer() {
  const navigate = useNavigate();

  return (
    <div className="mx-auto w-full max-w-4xl space-y-8">
      <div>
        <h1 className="text-token-2xl font-bold">Welcome to the DraftFC Creator Hub</h1>
        <p className="mt-1 max-w-2xl text-token-md text-muted-foreground">
          Everything you need to share your FPL Draft expertise with DraftFC members — and
          get paid for the audience you bring.
        </p>
      </div>

      {/* The commercial deal */}
      <Card className="border-primary/40">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-token-lg">
            <Banknote className="h-5 w-5 text-primary" aria-hidden="true" />
            The deal
          </CardTitle>
          <CardDescription>What you earn as a DraftFC creator.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="list-disc space-y-2 pl-5 text-token-md">
            <li>
              <strong>25% lifetime commission</strong> on every payment made by members who
              join through your referral link — not just their first payment.
            </li>
            <li>
              <strong>Paid weekly</strong> — every Monday, straight to your bank account via
              Stripe.
            </li>
            <li>
              <strong>No caps, no clawbacks</strong> — commission is earned on what your
              referrals actually pay, for as long as they stay subscribed.
            </li>
          </ul>

          <div>
            <p className="mb-2 text-token-md font-semibold">
              What your audience gets when they use your link:
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pack</TableHead>
                    <TableHead>Normal price</TableHead>
                    <TableHead>With your link</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Monthly Pass</TableCell>
                    <TableCell>£4.99/month</TableCell>
                    <TableCell className="font-medium">First month free</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Pre-Season + Monthly</TableCell>
                    <TableCell>£14.99 + £4.99/month</TableCell>
                    <TableCell className="font-medium">
                      £14.99 + first month free
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>

          <Button onClick={() => navigate('/referrals')}>
            <Handshake aria-hidden="true" />
            Open the Referral Centre
            <ArrowRight aria-hidden="true" />
          </Button>
        </CardContent>
      </Card>

      {/* What you can do */}
      <div>
        <h2 className="mb-3 text-token-xl font-semibold">What you can do here</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {THINGS.map((t) => {
            const Icon = t.icon;
            return (
              <Card key={t.title} className="flex flex-col">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-token-md">
                    <Icon className="h-4 w-4 text-secondary" aria-hidden="true" />
                    {t.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col justify-between gap-3">
                  <p className="text-token-sm text-muted-foreground">{t.body}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-max"
                    onClick={() => navigate(t.action.path)}
                  >
                    {t.action.label}
                    <ArrowRight aria-hidden="true" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
